import React from 'react'
import { CButton, CCard, CCardBody, CCardHeader, CCol, CRow } from '@coreui/react'

/**
 * DatosDePago — tarjeta COMPARTIDA "Datos de pago": Bancolombia, QR Bre-B
 * (Davivienda/Daviplata) con su llave y giros internacionales, más el botón
 * "Subir Pago (Otros medios)" que abre el modal compartido
 * (components/SubirPagoModal).
 *
 * [RGM] 2026-10-23 (pedido Rafael): el bloque vivía SOLO en PagoPage y el
 * cliente que paga la mensualidad desde Configuración → Mi Cuenta → Pagos no
 * veía las otras formas de pago ni el QR. Se extrae tal cual y se reutiliza en
 * ambas pantallas (misma estructura y estilos, sin duplicar el contenido).
 */
interface DatosDePagoProps {
  /** Abre el modal "Subir pago de Suscripción" (comprobante de otro medio).
      [RGM] 2026-10-23 (pedido Rafael): OPCIONAL — en Mi Cuenta → Pagos el
      botón de subir pago ya existe arriba en la misma pestaña, así que la
      tarjeta se muestra SIN botón; en PagoPage sí lo lleva. */
  onSubirPago?: () => void
  /** [RGM] 2026-10-23 (pedido Rafael): 'split' = tarjeta a lo ancho con el
      contenido a la izquierda y el QR a la derecha (Mi Cuenta → Pagos);
      'stacked' = como se ve en PagoPage. */
  layout?: 'stacked' | 'split'
}

const DatosDePago: React.FC<DatosDePagoProps> = ({ onSubirPago, layout = 'stacked' }) => {
  const contenido = (
    <>
      <div className="fw-semibold">PAGOS EN COLOMBIA</div>
      <div>Cuenta de Ahorros Bancolombia: <strong>74116820670</strong></div>
      <div><strong>RAFAEL GRANADA MENDOZA</strong> — C.C. <strong>94469600</strong></div>
      <div className="mt-2 fw-semibold">GIROS INTERNACIONALES</div>
      <div>Western Unión — RAFAEL GRANADA MENDOZA — DESTINO: <strong>COLOMBIA</strong></div>
      {/* [RGM] 2026-10-23 (pedido Rafael): el giro internacional debe llegar
          LIBRE de comisiones — el valor recibido tiene que ser el total
          exacto de la mensualidad; los costos del giro los asume quien envía. */}
      <div className="text-body-secondary small mt-1">
        El envío debe llegar LIBRE de comisiones: el valor recibido debe ser el
        total exacto de la mensualidad; los costos del giro los asume quien envía.
      </div>
      {/* [RGM] Pagó por OTRO medio (transferencia/giro): abre el modal
          COMPARTIDO (components/SubirPagoModal) — el pago queda
          pendiente de confirmación del superadmin. Solo se muestra cuando la
          pantalla lo pide (PagoPage); en Mi Cuenta el botón vive arriba. */}
      {onSubirPago && (
        <div className="mt-3">
          <CButton color="primary" variant="outline" onClick={onSubirPago}>
            Subir Pago (Otros medios)
          </CButton>
          <div className="text-body-secondary small mt-1">
            Transferencia o giro: subí tu comprobante y queda pendiente de confirmación del administrador
          </div>
        </div>
      )}
    </>
  )

  const qr = (
    <div className={layout === 'split' ? 'text-center' : 'mt-3'}>
      <div className="fw-semibold">PAGA ESCANEANDO EL QR (BRE-B)</div>
      <div className="text-body-secondary small">
        Desde tu app bancaria (Davivienda, Daviplata, Nequi o cualquier
        entidad habilitada) o a la llave <strong>@DAVICYBERTRONICA</strong>
      </div>
      <img
        src="/qr-bre-b-davivienda.png"
        alt="QR Bre-B Davivienda — CyberTronica (@DAVICYBERTRONICA)"
        className="rounded-3 border mt-2"
        style={{ maxWidth: layout === 'split' ? 280 : 260, width: '100%' }}
      />
    </div>
  )

  return (
    <CCard className="border-0 shadow-sm h-100">
      <CCardHeader className="fw-semibold">Datos de pago</CCardHeader>
      <CCardBody style={{ fontSize: 14 }}>
        {layout === 'split' ? (
          <CRow className="g-3 align-items-start">
            <CCol md={7}>{contenido}</CCol>
            <CCol md={5}>{qr}</CCol>
          </CRow>
        ) : (
          <>
            {contenido}
            {qr}
          </>
        )}
      </CCardBody>
    </CCard>
  )
}

export default DatosDePago
